import { DateTimePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField
} from "@mui/material";
import axios from "axios";
import { createForm } from "rc-form";
import * as React from "react";
import { Config } from "../../config/config";
import { Helpers } from "../../utils/helpers";
import { PopupMessage } from "../../utils/message";

interface ABVersions {
    name: string;
    quantity: number;
}

interface NewFileSate {
    visible: boolean;
    errors: any;
    creatingInProgress: boolean;
    versions: ABVersions[];
}

interface NewFileProps {
    form: any;
    activeEnv: any;
    refreshData(): void;
    type: "new" | "edit";
    defaultValues: any;
}

class ABTestingCreateTestWrapped extends React.Component<NewFileProps, NewFileSate> {

    constructor(props) {
        super(props);

        this.state = {
            visible: false,
            errors: {},
            creatingInProgress: false,
            versions: Object.keys(props.defaultValues?.pending || {}).map((key) => ({
                name: key,
                quantity: props.defaultValues.pending[key],
            })),
        };
    }

    onModalClose() {
        this.setState({ visible: false });
    }

    onModalAccept() {
        const { form } = this.props;

        form.validateFields((err, values) => {
            if (err) {
                this.setState({ errors: err });
                return;
            }

            this.setState({ creatingInProgress: true }, async () => {
                try {
                    const response = await axios.post(`${Config.getInstance.apiUrl}/api/abtests/createOrEdit`, {
                        ...values,
                        type: this.props.type,
                        id: this.props.defaultValues?.id,
                    }, {
                        headers: {
                            Authorization: Helpers.getSessionId(),
                        },
                    });

                    this.props.refreshData();

                    this.setState({ creatingInProgress: false, visible: false });
                } catch (error) {
                    this.setState({ creatingInProgress: false });

                    PopupMessage.error("Something went wrong!");
                }
            });
        });
    }

    showModal() {
        this.setState({ visible: true });
    }

    addVersion() {
        const { versions } = this.state;

        versions.push({
            name: this.state.versions.length.toString(),
            quantity: 0,
        });

        this.setState({versions});
    }

    listVersions() {
        const { getFieldDecorator, getFieldError } = this.props.form;

        return this.state.versions.map( (version, index) => (<Grid key={version.name} item xs={3} style={{ marginBottom: 15 }}>
            {getFieldDecorator("version_" + index,
                {
                    rules: [{
                        required: false,
                        message: "Version " + index,
                    }],
                    initialValue: version?.name || "",
                },
            )(
                <TextField
                    label="Version"
                    style={{ width: "80%", marginBottom: 10 }}
                    error={getFieldError("version_" + index)}
                    helperText={getFieldError("version_" + index) ? getFieldError("version_" + index).join(",") : null}
                />
            )}

            {getFieldDecorator("quantity_" + index,
                {
                    rules: [{
                        required: false,
                        message: "Quantity for version " + index,
                    }],
                    initialValue: version.quantity,
                },
            )(
                <TextField
                    label="Quantity"
                    style={{ width: "80%" }}
                    error={getFieldError("quantity_" + index)}
                    helperText={getFieldError("quantity_" + index) ? getFieldError("quantity_" + index).join(",") : null}
                />
            )}
        </Grid>) );
    }

    getButton(type: "new" | "edit") {
        const types = {
            new: {name: "Add new AB test"},
            edit: {name: "Edit"},
        };

        return <Button disableElevation variant="contained" size="small" onClick={() => this.showModal()}>
            {types[type].name}
        </Button>;
    }

    render() {
        const { getFieldDecorator, getFieldError } = this.props.form;

        return (
            <React.Fragment>
                {this.getButton(this.props.type)}

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Dialog
                        open={this.state.visible}
                        onClose={() => this.onModalClose()}
                        aria-labelledby="alert-dialog-title"
                        fullWidth={true}
                        maxWidth="md"
                    >
                        <DialogTitle id="alert-dialog-title">AB test</DialogTitle>
                        <DialogContent style={{ paddingTop: 10 }}>
                            <Grid container spacing={3} className="marginBottom--none">
                                <Grid item xs={6}>
                                    {getFieldDecorator("name",
                                        {
                                            rules: [{
                                                required: true,
                                                message: "Please enter test name",
                                            }],
                                            initialValue: this.props.defaultValues?.name || "",
                                        },
                                    )(
                                        <TextField
                                            label="Name"
                                            style={{ width: "100%" }}
                                            error={getFieldError("name")}
                                            helperText={getFieldError("name") ? getFieldError("name").join(",") : null}
                                            size="small"
                                        />,
                                    )}
                                </Grid>

                                <Grid item xs={6}>
                                    <FormControl>
                                        <InputLabel id="type-select">Status</InputLabel>

                                        {getFieldDecorator("status", {
                                            rules: [{
                                                required: true,
                                                message: "Please select status",
                                            }],
                                            initialValue: this.props.defaultValues?.status || "inactive",
                                        })(
                                            <Select labelId="type-select" autoWidth={true} size="small" style={{ width: "100%" }}>
                                                <MenuItem value="inactive">Inactive</MenuItem>
                                                <MenuItem value="run">Run</MenuItem>
                                                <MenuItem value="done">Done</MenuItem>
                                            </Select>
                                        )}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12}>
                                    {getFieldDecorator("description", {
                                        rules: [{
                                                required: false,
                                                message: "Description of test",
                                        }],
                                        initialValue: this.props.defaultValues?.description || "",
                                    },
                                    )(
                                        <TextField
                                            label="Description"
                                            style={{ width: "100%" }}
                                            multiline={true}
                                            error={getFieldError("description")}
                                            helperText={getFieldError("description") ? getFieldError("description").join(",") : null}
                                            size="small"
                                        />
                                    )}
                                </Grid>

                                <Grid item xs={6}>
                                    {getFieldDecorator("start_time", {
                                        rules: [{
                                            required: false,
                                            message: "Test start time",
                                        }],
                                        initialValue: new Date(this.props.defaultValues?.start_time || Date.now()),
                                    },
                                    )(
                                        <DateTimePicker
                                            label="Start date"
                                            renderInput={(params) => <TextField {...params} size="small" style={{ width: "100%" }}/>}
                                            ampm={false}
                                        />
                                    )}
                                </Grid>

                                <Grid item xs={6}>
                                    {getFieldDecorator("end_time", {
                                        rules: [{
                                            required: false,
                                            message: "Test end time"
                                        }],
                                        initialValue: new Date(this.props.defaultValues?.end_time || Date.now()),
                                    },
                                    )(
                                        <DateTimePicker
                                            label="End date"
                                            renderInput={(params) => <TextField {...params} size="small" style={{ width: "100%" }}/>}
                                            ampm={false}
                                        />
                                    )}
                                </Grid>

                                <Grid item xs={12}>
                                    <h3 style={{ margin: 0, marginLeft: 10, marginTop: 15 }}>Versions</h3>
                                </Grid>

                                <Grid item xs={12}>
                                    <Grid container style={{ padding: 12 }}>
                                        {this.listVersions()}
                                    </Grid>
                                </Grid>

                                <Grid item xs={12}>
                                    <Button disableElevation variant="contained" onClick={() => this.addVersion()}>Add version</Button>
                                </Grid>

                                <Grid item xs={12}>
                                    {getFieldDecorator("results", {
                                        rules: [{
                                            required: false,
                                            message: "Results of test",
                                        }],
                                        initialValue: this.props.defaultValues?.results || "",
                                    },
                                    )(
                                        <TextField
                                            label="Results"
                                            style={{ width: "100%" }}
                                            error={getFieldError("results")}
                                            multiline
                                            helperText={getFieldError("results") ? getFieldError("results").join(",") : null}
                                            size="small"
                                        />
                                    )}
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                disableElevation
                                onClick={() => this.onModalClose()}
                                disabled={this.state.creatingInProgress}
                                size="small"
                                variant="outlined"
                                style={{ marginRight: 10 }}
                            >
                                Cancel
                            </Button>

                            <div className="loading--button-wrapper">
                                <Button
                                    disableElevation
                                    onClick={() => this.onModalAccept()}
                                    color="primary"
                                    disabled={this.state.creatingInProgress}
                                    size="small"
                                    variant="contained"
                                >
                                    Save
                                </Button>

                                {this.state.creatingInProgress && <CircularProgress size={24} className="loading--button-progress"/>}
                            </div>
                        </DialogActions>
                    </Dialog>
                </LocalizationProvider>
            </React.Fragment>
        );
    }
}

export const ABTestingCreateTest = createForm()(ABTestingCreateTestWrapped);
